import type {
  Item,
  CreateItemRequest,
  ApiResponse,
  Spark,
  CreateSparkRequest,
  CreateSparkResponse,
  ThemeKey,
  SparkReview,
  VoteType,
} from "@sparklab/types";
import { encodeSparkId } from "../utils/sparkId";

// 支持通过环境变量配置 API 基础 URL
// 开发环境：使用相对路径 /api（通过 Vite 代理或 _redirects）
// 生产环境：使用相对路径 /api（通过 Cloudflare Pages _redirects 代理到 Worker）
// 如果需要直接调用 Worker URL，可以设置 VITE_API_BASE=https://sparklab-api.zjinys.workers.dev
const API_BASE = import.meta.env.VITE_API_BASE || "/api";

class ApiError extends Error {
  constructor(message: string, public status?: number) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const error: ApiResponse = await response.json().catch(() => ({
      success: false,
      error: `HTTP ${response.status}: ${response.statusText}`,
    }));
    throw new ApiError(error.error || `请求失败: ${response.statusText}`, response.status);
  }

  const data: ApiResponse<T> = await response.json();
  if (!data.success) {
    throw new ApiError(data.error || "请求失败");
  }

  return data.data as T;
}

export interface User {
  id: number;
  user_name: string;
  points: number;
  created_at: string;
  theme: ThemeKey;
}

export interface RegisterRequest {
  user_name: string;
  password: string;
  invite_code: string;
}

export interface LoginRequest {
  user_name: string;
  password: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

function requireUserId(): string {
  const userId = localStorage.getItem("userId");
  if (!userId) {
    throw new Error("未登录");
  }
  return userId;
}

function getOptionalUserId(): string | null {
  return localStorage.getItem("userId");
}

function toSparkIdParam(id: number | string): string {
  if (typeof id === "string") {
    if (id.startsWith("sp_")) {
      return id;
    }
    const parsed = Number.parseInt(id, 10);
    if (!Number.isNaN(parsed)) {
      return encodeSparkId(parsed);
    }
    return id;
  }
  return encodeSparkId(id);
}

export const api = {
  async getItems(): Promise<Item[]> {
    return request<Item[]>("/data");
  },

  async getItem(id: number): Promise<Item> {
    return request<Item>(`/data/${id}`);
  },

  async getSpark(id: number | string): Promise<Spark> {
    const userId = getOptionalUserId();
    const encodedId = toSparkIdParam(id);
    // id 可以是数字（向后兼容）或编码后的字符串
    return request<Spark>(`/sparks/${encodedId}`, {
      headers: userId
        ? {
            "X-User-Id": userId,
          }
        : undefined,
    });
  },

  async createItem(item: CreateItemRequest): Promise<Item> {
    return request<Item>("/data", {
      method: "POST",
      body: JSON.stringify(item),
    });
  },

  async deleteItem(id: number): Promise<void> {
    await request<void>(`/data/${id}`, {
      method: "DELETE",
    });
  },

  // 用户认证相关
  async register(data: RegisterRequest): Promise<User> {
    return request<User>("/auth/register", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  async login(data: LoginRequest): Promise<User> {
    return request<User>("/auth/login", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  async getCurrentUser(): Promise<User> {
    const userId = requireUserId();
    return request<User>("/auth/me", {
      headers: {
        "X-User-Id": userId,
      },
    });
  },

  async createMagic(prompt: string): Promise<{ html: string }> {
    const userId = requireUserId();
    return request<{ html: string }>("/create-magic", {
      method: "POST",
      headers: {
        "X-User-Id": userId,
      },
      body: JSON.stringify({ prompt }),
    });
  },

  async saveSpark(payload: CreateSparkRequest): Promise<CreateSparkResponse> {
    const userId = requireUserId();

    return request<CreateSparkResponse>("/sparks", {
      method: "POST",
      headers: {
        "X-User-Id": userId,
      },
      body: JSON.stringify(payload),
    });
  },

  async getMySparks(): Promise<Spark[]> {
    const userId = requireUserId();

    return request<Spark[]>("/sparks", {
      headers: {
        "X-User-Id": userId,
      },
    });
  },

  async getPublicSparks(limit = 10): Promise<Spark[]> {
    const params = new URLSearchParams({ limit: String(limit) });
    const query = params.toString();
    return request<Spark[]>(`/sparks/public?${query}`);
  },

  async deleteSpark(id: number | string): Promise<void> {
    const userId = requireUserId();
    const encodedId = toSparkIdParam(id);
    await request(`/sparks/${encodedId}`, {
      method: "DELETE",
      headers: {
        "X-User-Id": userId,
      },
    });
  },

  async publishSpark(id: number | string, isPublic = true): Promise<Spark> {
    const userId = requireUserId();
    const encodedId = toSparkIdParam(id);
    return request<Spark>(`/sparks/${encodedId}/public`, {
      method: "PUT",
      headers: {
        "X-User-Id": userId,
      },
      body: JSON.stringify({ is_public: isPublic }),
    });
  },

  async getSparkReviews(id: number | string): Promise<SparkReview[]> {
    const userId = requireUserId();
    const encodedId = toSparkIdParam(id);
    return request<SparkReview[]>(`/sparks/${encodedId}/reviews`, {
      headers: {
        "X-User-Id": userId,
      },
    });
  },

  async addSparkComment(id: number | string, comment: string): Promise<SparkReview> {
    const userId = requireUserId();
    const encodedId = toSparkIdParam(id);
    return request<SparkReview>(`/sparks/${encodedId}/comments`, {
      method: "POST",
      headers: {
        "X-User-Id": userId,
      },
      body: JSON.stringify({ comment }),
    });
  },

  async voteSpark(id: number | string, vote: VoteType): Promise<Spark> {
    const userId = requireUserId();
    const encodedId = toSparkIdParam(id);
    return request<Spark>(`/sparks/${encodedId}/vote`, {
      method: "POST",
      headers: {
        "X-User-Id": userId,
      },
      body: JSON.stringify({ vote }),
    });
  },

  async updateTheme(theme: ThemeKey): Promise<User> {
    const userId = requireUserId();
    return request<User>("/auth/theme", {
      method: "PUT",
      headers: {
        "X-User-Id": userId,
      },
      body: JSON.stringify({ theme }),
    });
  },

  async changePassword(payload: ChangePasswordRequest): Promise<void> {
    const userId = requireUserId();
    await request("/auth/password", {
      method: "PUT",
      headers: {
        "X-User-Id": userId,
      },
      body: JSON.stringify(payload),
    });
  },

  async logout(): Promise<void> {
    try {
      await request("/auth/logout", { method: "POST" });
    } catch {
      // ignore logout errors
    }
  },
};

